import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Chip,
    CircularProgress,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import RequestService from '../../services/RequestService';
import { shouldHideEmail } from '../../utils/emailUtils';

const UserEditDialog = ({ open, onClose, user, player, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        nickname: '',
        password: '',
        role: '',
        groupIds: [],
    });

    const permissionLabels = {
        USER_VIEW: 'Visualizar Usuário',
        USER_CREATE: 'Criar Usuário',
        USER_UPDATE: 'Editar Usuário',
        USER_DELETE: 'Excluir Usuário',
        USER_AWARD: 'Premiar Usuário',
        USER_PENALTY: 'Penalizar Usuário',
        CARDS_VIEW: 'Visualizar Cartões',
        CARDS_ASSIGN: 'Atribuir Cartões',
        GROUP_VIEW: 'Visualizar Grupos',
        GROUP_CREATE: 'Criar Grupo',
        GROUP_UPDATE: 'Editar Grupo',
        GROUP_DELETE: 'Excluir Grupo',
        GAME_VIEW: 'Visualizar Jogos',
        GAME_CREATE: 'Criar Jogo',
        GAME_UPDATE: 'Editar Jogo',
        GAME_DELETE: 'Excluir Jogo',
        CONTENT_VIEW: 'Visualizar Conteúdos',
        CONTENT_CREATE: 'Criar Conteúdo',
        CONTENT_UPDATE: 'Editar Conteúdo',
        CONTENT_DELETE: 'Excluir Conteúdo',
        CONTENT_APPROVE: 'Aprovar Conteúdo',
        CONTENT_REVIEW: 'Revisar Comprovações',
        STORE_VIEW: 'Visualizar Lojas',
        STORE_CREATE: 'Criar Loja',
        STORE_UPDATE: 'Editar Loja',
        STORE_DELETE: 'Excluir Loja',
        STORE_APPROVE: 'Aprovar Retirada',
        SETTINGS_VIEW: 'Visualizar Configurações',
        QUIZ_VIEW: 'Visualizar Quiz',
        QUIZ_CREATE: 'Criar Quiz',
        QUIZ_UPDATE: 'Editar Quiz',
        QUIZ_DELETE: 'Excluir Quiz',
    };

    const [groups, setGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [error, setError] = useState(null);
    const [permissionTypes, setPermissionTypes] = useState([]);

    useEffect(() => {
        if (player && open && user.company?.id) {
            const fetchGroups = async () => {
                setError(null);
                setLoadingGroups(true);
                try {
                    const groupData = await RequestService.getGroups(
                        user.company.id
                    );
                    setGroups(Array.isArray(groupData) ? groupData : []);

                    setFormData({
                        name: player.name || '',
                        email: player.email || '',
                        role: player.role || '',
                        nickname: player.nickname || '',
                        groupIds: player.groups
                            ? player.groups.map((group) => group.id)
                            : [],
                    });

                    setPermissionTypes(player.permissions || []);
                } catch (err) {
                    setError('Erro ao carregar grupos.');
                } finally {
                    setLoadingGroups(false);
                }
            };

            fetchGroups();
        }
    }, [player, open, user.company?.id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleGroupsChange = (event) => {
        setFormData((prev) => ({
            ...prev,
            groupIds: event.target.value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                userId: player.id,
                name: formData.name,
                email: formData.email,
                nickname: formData.nickname,
                role: formData.role,
                groupIds: formData.groupIds,
                permissionTypes,
            };

            if (
                typeof formData.password === 'string' &&
                formData.password.trim() !== ''
            ) {
                payload.password = formData.password;
            }

            await RequestService.editPlayer(payload);

            onSuccess();
            onClose();
        } catch (err) {
            console.error('Error updating user:', err);
            setError('Erro ao atualizar usuário. Verifique os dados.');
        }
    };

    const handlePermissionChange = (permission) => {
        setPermissionTypes((prev) =>
            prev.includes(permission)
                ? prev.filter((p) => p !== permission)
                : [...prev, permission]
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Editar Usuário</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle1">
                        Dados do Usuário
                    </Typography>
                    <TextField
                        label="Nome"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        label="Apelido"
                        name="nickname"
                        value={formData.nickname}
                        onChange={handleChange}
                        fullWidth
                    />
                    {!shouldHideEmail(formData.email, user?.company?.name) && (
                        <TextField
                            label="E-mail"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            fullWidth
                        />
                    )}
                    <TextField
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        select
                        SelectProps={{ native: true }}
                        fullWidth
                    >
                        <option value="">Selecione</option>
                        <option value="ADMIN">Administrador</option>
                        <option value="MODERATOR">Moderador</option>
                        <option value="USER">Usuário</option>
                    </TextField>

                    {formData.role !== 'ADMIN' && (
                        <TextField
                            label="Alterar Senha"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            fullWidth
                        />
                    )}

                    {loadingGroups ? (
                        <CircularProgress />
                    ) : (
                        <FormControl fullWidth>
                            <InputLabel>Grupos</InputLabel>
                            <Select
                                multiple
                                value={formData.groupIds}
                                onChange={handleGroupsChange}
                                input={<OutlinedInput label="Grupos" />}
                                renderValue={(selected) =>
                                    Array.isArray(groups)
                                        ? groups
                                              .filter((group) =>
                                                  selected.includes(group.id)
                                              )
                                              .map((group) => (
                                                  <Chip
                                                      key={group.id}
                                                      label={group.name}
                                                  />
                                              ))
                                        : []
                                }
                            >
                                {Array.isArray(groups) &&
                                    groups.map((group) => (
                                        <MenuItem
                                            key={group.id}
                                            value={group.id}
                                        >
                                            {group.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}

                    {formData.role === 'MODERATOR' && (
                        <Box
                            sx={{
                                maxHeight: 300,
                                overflowY: 'auto',
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                padding: 2,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Permissões
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    columnGap: 2,
                                    rowGap: 1,
                                }}
                            >
                                {Object.keys(permissionLabels).map(
                                    (permission) => (
                                        <FormControlLabel
                                            key={permission}
                                            control={
                                                <Checkbox
                                                    checked={permissionTypes.includes(
                                                        permission
                                                    )}
                                                    onChange={() =>
                                                        handlePermissionChange(
                                                            permission
                                                        )
                                                    }
                                                />
                                            }
                                            label={permissionLabels[permission]}
                                        />
                                    )
                                )}
                            </Box>
                        </Box>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Fechar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                >
                    Salvar Alterações
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserEditDialog;
